/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink as RouterLink, useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import EditIcon from '@material-ui/icons/Edit';
import DashboardIcon from '@material-ui/icons/Dashboard';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import UndoIcon from '@material-ui/icons/Undo';
import PublishIcon from '@material-ui/icons/Publish';
import SendIcon from '@material-ui/icons/Send';
import FolderIcon from '@material-ui/icons/Folder';
import ErrorIcon from '@material-ui/icons/ErrorRounded';
import CreditCard from '@material-ui/icons/CreditCard';
import ViewIcon from '@material-ui/icons/Visibility';
import ImportExport from '@material-ui/icons/ImportExport';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { useDispatch, useSelector } from 'react-redux';
import { TableQueryService } from 'shared/navigation/TableQueryService';
import { People, SwapVert } from '@material-ui/icons';
import _ from 'lodash';
import { callApi } from 'api/helpers/api.helpers';
import { submission } from 'components/draft/attachments/services/attachmentsMocks';
import clsx from 'clsx';
import {
  selectAutofillEnabled,
  selectProcedureResourceEnabled,
  selectVersion,
} from '../../redux/features/configuration/configuration.slice';
import draftApi from '../../api/draft.api';
import ePCTApi from '../../api/ePCT.api';
import { Roles } from '../../shared/enums/Roles';
import { SubmissionStatus, getSubmissionStatusById } from '../../models/SubmissionStatus';
import { ProcedureCategory } from '../../procedure/SubmissionProcedure';
import { deleteCookie, getCookie, setCookie } from '../../shared/utils/cookie';
import { flattenObject, getSearchPageByProcedure, isLockError } from '../../shared/utils/objectUtils';

import { IsInRole } from '../../shared/authorization/IsInRole';

import Button from '../../components/common/Button';
import addressBookApi from '../../api/addressBook.api';
import useDeleteConfirmation from '../../components/common/useDeleteConfirmation';
import { selectAuthUserData } from '../../redux/features/auth/auth.slice';
import { startApplicationLoading, stopApplicationLoading } from '../../redux/features/application/application.slice';
import useProcedure from '../../procedure/useProcedure';
import useSubmissionActions from '../../shared/authorization/useSubmissionActions';
import useWebsockets from '../../api/websockets/useWebsockets';
import { selectSubmissionsData, submissionUpdated } from '../../redux/features/submissions/submissions.slice';
import { fetchSubmissionRows } from '../../redux/features/submissions/submissions.thunk';
import useAutofill from '../../redux/features/autofill/useAutofill';
import { postProcess } from '../../shared/configuration/businessRules';
import GenericCellDataAdapter from '../../components/foTable/adapters/GenericCellDataAdapter';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';
import { RoutePath } from '../../shared/enums/RoutePath';
import { useSubsequentActions } from '../../shared/subsequentActions/useSubsequentActions';
import { useScreenSize } from '../../shared/theming/useScreenSize';
import useTableConfiguration from '../../components/foTable/useTableConfiguration';
import { useTheming } from '../../shared/theming/useTheming';
import { SORT_ORDER } from '../../components/foTable/utils/enums';
import { CELL_TYPES } from '../../components/foTable/foTableBuilder/FOCellTypes';
import { dataTestId } from '../../shared/utils/versionUtils';
import useProcedures from '../../procedure/useProcedures';
import { openDialog } from '../../redux/features/dialogs/dialog.slice';
import SignIcon from '../../components/icons/SignIcon';
import { getResendSigningEmailsThunk } from '../../redux/features/submission/submission.thunk.signing';
import PackageIcon from '../../components/icons/PackageIcon';
import useSubmissionTransfer from '../../components/submission/useSubmissionTransfer';
import useAlertDialog from '../../redux/features/alertDialog/useAlertDialog';

const useStyles = makeStyles(theme => ({
  importButton: {
    margin: '0.5rem',
  },
  newContactButton: {
    margin: '0.5rem',
  },
  emptyTableIcon: {
    margin: '0 0 1rem 0',
    fontSize: '5rem',
  },
  errorTableIcon: {
    margin: '0 0 1rem 0',
    fontSize: '5rem',
    color: theme.palette.error.main,
  },
  createButton: {},
  maxWidth: {
    width: '100%',
  },
}));

export const DRAFT_VIEW = {
  GRID: 'GRID_VIEW',
  TABLE: 'TABLE_VIEW',
};

export const PAGE_SIZE_GRID = 4;
export const PAGE_SIZES = [
  10,
  15,
  20,
  25,
  50,
  100,
];

export default function useDraftManagement(procedureId) {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userData = useSelector(selectAuthUserData);
  const location = useLocation();
  const theming = useTheming();
  const {
    isLocked,
    canEdit,
    canDelete,
    canChangeStatus,
    canRollbackToDraft,
    canRollbackToReadyToSign,
  } = useSubmissionActions();

  const { isSmallOrBigger, isMediumOrBigger, isLargeOrBigger, isExtraLargeOrBigger } = useScreenSize();
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(true);
  const [emptyTableWarningMessage, setEmptyTableWarningMessage] = useState(null);
  const [emptyTableSuggestion, setEmptyTableSuggestion] = useState(null);

  const [anchorViewMenu, setAnchorViewMenu] = useState(null);
  const [anchorActionMenu, setAnchorActionMenu] = useState(null);
  const [anchorChangeStatusMenu, setAnchorChangeStatusMenu] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [actionEntries, setActionEntries] = useState([]);
  const [changeStatusEntries, setChangeStatusEntries] = useState([]);
  const [selection, setSelection] = useState([]);
  const [view, setView] = useState(!isLargeOrBigger ? DRAFT_VIEW.GRID : DRAFT_VIEW.TABLE);
  const viewPrevious = useRef();
  const [sendSubmissionRequestProgress, setSendSubmissionRequestProgess] = useState(null);
  const [isChangeStatusDialogOpen, setIsChangeStatusDialogOpen] = useState(false);
  const [isSendSubmissionDialogOpen, setSendSubmissionDialogOpen] = useState(false);
  const [isCreateEPCTDialogOpen, setIsCreateEPCTDialogOpen] = useState(false);
  const [isCreateSPCDialogOpen, setIsCreateSPCDialogOpen] = useState(false);
  const [isCreateEPDialogOpen, setIsCreateEPDialogOpen] = useState(false);
  const [isSendSubmissionProcessDialogOpen, setSendSubmissionProcessDialogOpen] = useState(false);
  const [isRevertStatusDialogOpen, setIsRevertStatusDialogOpen] = useState(false);
  const [revertStatusAction, setRevertStatusAction] = useState(null);

  const [dialogValues, setDialogValues] = useState([]);
  const [fromStatus, setFromStatus] = useState(null);
  const [toStatus, setToStatus] = useState(null);

  const [showCommentsSection, setShowCommentsSection] = useState(false);
  const [comments, setComments] = useState(null);

  const [sort, setSort] = useState({ sortColumn: null, sortOrder: null });
  const [showImportContactDialog, setShowImportContactDialog] = useState(false);

  const currentProcedure = useProcedure(procedureId);
  const { getProcedure } = useProcedures();
  const { initialSubmissionProcedure } = useParams();
  const configurationVersion = useSelector(selectVersion);
  const isWipoPage =
    currentProcedure.category === ProcedureCategory.WIPO_PROCEDURE ||
    currentProcedure.category === ProcedureCategory.WIPO_SUBSEQUENT_ACTIONS;
  const isSpc = currentProcedure.category === ProcedureCategory.SPC_PROCEDURE;
  const isEPPage = currentProcedure.category === ProcedureCategory.EP_PROCEDURE;
  const isEpvalPage = currentProcedure.category === ProcedureCategory.EP_VALIDATION_PROCEDURE;
  const isAddressBookPage = currentProcedure.category.includes('addressbook');
  const tableQueryService = new TableQueryService(history, location);

  const submissionsData = useSelector(selectSubmissionsData);
  const subsequentActions = useSubsequentActions(initialSubmissionProcedure ?? currentProcedure.id);

  const isAutofillEnabled = useSelector(state => selectAutofillEnabled(state, currentProcedure.id));
  const isCreationDialogEnabled = useSelector(state =>
    selectProcedureResourceEnabled(state, currentProcedure.id, ConfigResourceProps.CREATION_DIALOG_UI_SCHEMA.name),
  );

  const frontendCustomisationConfig = useConfiguration(ConfigResourceProps.FRONTEND_CUSTOMISATION);
  const tableConfig = isAddressBookPage
    ? ConfigResourceProps.ADDRESSBOOK_TABLE_CONFIGURATION
    : ConfigResourceProps.SUBMISSION_TABLE_CONFIGURATION;
  const tableConfiguration = useTableConfiguration(tableConfig, !isAddressBookPage ? currentProcedure.id : null);
  const { columns: columnsConfiguration } = tableConfiguration;

  const { updateAutofillDialogState } = useAutofill(procedureId);
  const { openAlertDialog } = useAlertDialog();

  const isCloneEnabled = () => {
    return frontendCustomisationConfig?.resource?.cloneEnabled ?? false;
  };

  const hasRequiredRoles = (requiredRoles) => {
    return userData?.roles.some(role => requiredRoles.includes(role));
  };

  useWebsockets({
    topics: ['submission-update'],
    onMessageReceived: (message) => {
      // test if we could merge to rows and rerender correctly
      dispatch(submissionUpdated(message));
    },
  });

  useEffect(() => {
    if (submissionsData.rowItems && submissionsData.rowItems.length > 0) {
      const tableData = createTableData(submissionsData.rowItems);
      setRows(tableData);
      setData(submissionsData.rowItems);
    }
  }, [submissionsData.rowItems]);

  const AdapterLink = React.forwardRef((props, ref) => <RouterLink innerRef={ref} {...props} />);

  const CreateStandardProcedureButton = (params) => {
    const title = params.title || t('submission.create');
    const to =
      params.to ||
      RoutePath.generateRoutePath(RoutePath.PROCEDURE_DRAFT, {
        procedure: currentProcedure.id,
      });
    return (
      <IsInRole roles={[Roles.SUBMISSION_DRAFTER]}>
        <Button
          {...dataTestId({ value: `createSubmissionButton-NP--${configurationVersion.value}` })}
          className={clsx(classes.createButton, { [classes.maxWidth]: !!procedureId })}
          color="primary"
          to={to}
          component={AdapterLink}
          startIcon={<AddCircleOutlineIcon />}
          disableElevation
        >
          {title}
        </Button>
      </IsInRole>
    );
  };

  const CreateStandardProcedureWithDialogButton = (params) => {
    const title = params.title || t('submission.create');
    return (
      <IsInRole roles={[Roles.SUBMISSION_DRAFTER]}>
        <Button
          {...dataTestId({ value: `createSubmissionButton-${currentProcedure.id}-${configurationVersion.value}` })}
          className={clsx(classes.createButton, { [classes.maxWidth]: !!procedureId })}
          color="primary"
          onClick={() =>
            dispatch(
              openDialog({
                dialogProcedure: currentProcedure,
                uiSchemaProps: ConfigResourceProps.CREATION_DIALOG_UI_SCHEMA,
                validationSchemaProps: ConfigResourceProps.CREATION_DIALOG_VALIDATION_SCHEMA,
              }),
            )
          }
          startIcon={<AddCircleOutlineIcon />}
        >
          {title}
        </Button>
      </IsInRole>
    );
  };

  const CreateSAButton = ({ id, color, className }) => {
    return (
      <IsInRole roles={[Roles.SUBMISSION_DRAFTER]}>
        <Button
          id={`${id}-submission.subsequent-action.create`}
          color={color ?? 'default'}
          className={clsx(classes.createButton, className)}
          onClick={() =>
            dispatch(
              openDialog({
                dialogProcedure: subsequentActions.initialProcedure ?? currentProcedure,
                uiSchemaProps: ConfigResourceProps.SUBMISSION_SA_UI_SCHEMA,
                validationSchemaProps: ConfigResourceProps.SUBMISSION_SA_VALIDATION_SCHEMA,
              }),
            )
          }
          startIcon={<AddCircleOutlineIcon />}
        >
          {t('submission.subsequent-action.create')}
        </Button>
      </IsInRole>
    );
  };

  const CreateEPVLProcedureButton = () => {
    return (
      <IsInRole roles={[Roles.SUBMISSION_DRAFTER]}>
        <Button
          {...dataTestId({ value: `createSubmissionButton-EPVL--${configurationVersion.value}` })}
          className={clsx(classes.createButton, { [classes.maxWidth]: !!procedureId })}
          color="primary"
          onClick={() => {
            return updateAutofillDialogState({ open: true });
          }}
          startIcon={<AddCircleOutlineIcon />}
          disableElevation
        >
          {t('submission.create')}
        </Button>
      </IsInRole>
    );
  };

  function CreateWipoButton() {
    return (
      <IsInRole roles={[Roles.SUBMISSION_DRAFTER]}>
        <Button
          {...dataTestId({ value: `createSubmissionButton-EPCT--${configurationVersion.value}` })}
          className={clsx(classes.createButton, { [classes.maxWidth]: !!procedureId })}
          color="primary"
          startIcon={<AddCircleOutlineIcon />}
          disableElevation
          onClick={() => setIsCreateEPCTDialogOpen(true)}
        >
          {t('submission.create')}
        </Button>
      </IsInRole>
    );
  }

  function CreateSpcButton() {
    return (
      <IsInRole roles={[Roles.SUBMISSION_DRAFTER]}>
        <Button
          {...dataTestId({ value: `createSubmissionButton-SPC--${configurationVersion.value}` })}
          className={clsx(classes.createButton, { [classes.maxWidth]: !!procedureId })}
          color="primary"
          startIcon={<AddCircleOutlineIcon />}
          disableElevation
          onClick={() =>
            dispatch(
              openDialog({
                dialogProcedure: currentProcedure,
                uiSchemaProps: ConfigResourceProps.SUBMISSION_SPC_UI_SCHEMA,
                validationSchemaProps: ConfigResourceProps.SUBMISSION_SPC_VALIDATION_SCHEMA,
              }),
            )
          }
        >
          {t('submission.create')}
        </Button>
      </IsInRole>
    );
  }

  function CreateEpoButton() {
    return (
      <IsInRole roles={[Roles.SUBMISSION_DRAFTER]}>
        <Button
          {...dataTestId({ value: `createSubmissionButton-EP--${configurationVersion.value}` })}
          createSubmissionButton
          color="primary"
          startIcon={<AddCircleOutlineIcon />}
          className={clsx(classes.createButton, { [classes.maxWidth]: !!procedureId })}
          disableElevation
          onClick={() => setIsCreateEPDialogOpen(true)}
        >
          {t('submission.create')}
        </Button>
      </IsInRole>
    );
  }

  function CreateContactButton() {
    return (
      <>
        <IsInRole
          key="importContactRoleWrapper"
          roles={[Roles.SUBMISSION_DRAFTER, Roles.SUBMISSION_SENDER, Roles.SUBMISSION_SIGNER]}
        >
          <Button
            className={classes.importButton}
            color="secondary"
            startIcon={<PublishIcon />}
            onClick={() => setShowImportContactDialog(true)}
          >
            {t('import.contact')}
          </Button>
          <Button
            key="createContact"
            {...dataTestId({ value: `createContactButton---${configurationVersion.value}` })}
            className={classes.newContactButton}
            color="primary"
            href="/addContact"
            to="/addressbook/contact"
            component={AdapterLink}
            startIcon={<AddCircleOutlineIcon />}
            disableElevation
          >
            {t('add.new.contact')}
          </Button>
        </IsInRole>
      </>
    );
  }

  const pageStructure = useMemo(() => {
    return {
      [ProcedureCategory.NATIONAL_PROCEDURE]: createNationalProcedurePageStructure(
        isCreationDialogEnabled ? <CreateStandardProcedureWithDialogButton /> : <CreateStandardProcedureButton />,
      ),
      [ProcedureCategory.EP_VALIDATION_PROCEDURE]: createNationalProcedurePageStructure(
        isAutofillEnabled ? <CreateEPVLProcedureButton /> : <CreateStandardProcedureButton />,
      ),
      [ProcedureCategory.SPC_PROCEDURE]: createNationalProcedurePageStructure(<CreateSpcButton />),
      [ProcedureCategory.SUBSEQUENT_ACTIONS]: createNationalProcedurePageStructure(
        <CreateSAButton id={initialSubmissionProcedure} color="primary" />,
      ),
      [ProcedureCategory.WIPO_SUBSEQUENT_ACTIONS]: createWipoProcedurePageStructure(
        <CreateSAButton id={initialSubmissionProcedure} color="primary" />,
      ),
      [ProcedureCategory.WIPO_PROCEDURE]: {
        title: t(currentProcedure.label),
        description: t(currentProcedure.description),
        button: <CreateWipoButton />,
        CreateSAButton,
        createSARow: row => createSASubmission(row),
        loadData: () => {
          dispatch(
            fetchSubmissionRows({
              procedure: !initialSubmissionProcedure ? currentProcedure.id : undefined,
              initialSubmissionProcedure,
              pagingSortingAndFiltering: {
                numberOfElementsOnAPage: pageSize,
                pageNumber: page,
                sortColumn: sort.sortColumn,
                sortOrder: sort.sortOrder,
                filter,
              },
            }),
          );
          const _procedure = !initialSubmissionProcedure ? currentProcedure.id : undefined;
          return draftApi.fetchSubmissionRows(_procedure, initialSubmissionProcedure, {
            numberOfElementsOnAPage: pageSize,
            pageNumber: page,
            sortColumn: sort.sortColumn,
            sortOrder: sort.sortOrder,
            filter,
          });
        },
        editRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
          history.push(
            RoutePath.generateRoutePath(RoutePath.EPCT_EDIT_SUBMISSION, { procedure, submissionId }),
            [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
          ),
        viewRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
          history.push(
            RoutePath.generateRoutePath(RoutePath.PROCEDURE_RECEIPTS_SUBMISSION, { submissionId, procedure }),
            [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
          ),
        previewRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
          history.push(
            RoutePath.generateRoutePath(RoutePath.PROCEDURE_PREVIEW_SUBMISSION, { procedure, submissionId }),
            [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
          ),
        summaryRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
          history.push(
            RoutePath.generateRoutePath(RoutePath.EPCT_EDIT_SUBMISSION, { procedure, submissionId }),
            [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
          ),
        deleteRow: ids => callApi(ePCTApi.deleteSubmissions)(ids),
        sendToSign: ids => callApi(ePCTApi.sendToSignSubmissions)(ids),
        transferSubmissions: (ids, newOrgId) =>
          callApi(draftApi.transferSubmissions, {
            onSuccess: () => transferConfirmationDialog.open(false),
            onError: () => transferConfirmationDialog.open(true),
            showAlertOnError: false,
          })(ids, newOrgId),
        setPage: (value) => {
          setCookie({ name: 'pageEPCT', value });
          setPage(value);
        },
        setPageSize: (value) => {
          setCookie({ name: 'pageSizeEPCT', value });
          setPageSize(value);
        },
        getPage: () => Number(getCookie('pageEPCT')) || 0,
        getPageSize: () => Number(getCookie('pageSizeEPCT')) || PAGE_SIZES[0],
        deletePageCookie: () => {
          deleteCookie('pageEPCT');
          deleteCookie('pageSizeEPCT');
        },
        emptyTableIcon: <FolderIcon color="secondary" className={classes.emptyTableIcon} />,
        errorTableIcon: <ErrorIcon color="secondary" className={classes.errorTableIcon} />,
        requestReceipt: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
          history.push(
            RoutePath.generateRoutePath(RoutePath.PROCEDURE_REQUEST_RECEIPT_SUBMISSION, { procedure, submissionId }),
            [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
          ),
      },
      [ProcedureCategory.EP_PROCEDURE]: {
        title: t(currentProcedure.label),
        description: t(currentProcedure.description),
        button: <CreateEpoButton />,
        loadData: () => {
          dispatch(
            fetchSubmissionRows({
              procedure: !initialSubmissionProcedure ? currentProcedure.id : undefined,
              initialSubmissionProcedure,
              pagingSortingAndFiltering: {
                numberOfElementsOnAPage: pageSize,
                pageNumber: page,
                sortColumn: sort.sortColumn,
                sortOrder: sort.sortOrder,
                filter,
              },
            }),
          );
          const _procedure = !initialSubmissionProcedure ? currentProcedure.id : undefined;
          return draftApi.fetchSubmissionRows(_procedure, initialSubmissionProcedure, {
            numberOfElementsOnAPage: pageSize,
            pageNumber: page,
            sortColumn: sort.sortColumn,
            sortOrder: sort.sortOrder,
            filter,
          });
        },
        editRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
          history.push(
            RoutePath.generateRoutePath(RoutePath.EP_EDIT_SUBMISSION, { procedure, submissionId }),
            [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
          ),
        viewRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
          history.push(
            RoutePath.generateRoutePath(RoutePath.PROCEDURE_RECEIPTS_SUBMISSION, { submissionId, procedure }),
            [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
          ),
        previewRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
          history.push(
            RoutePath.generateRoutePath(RoutePath.PROCEDURE_PREVIEW_SUBMISSION, { procedure, submissionId }),
            [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
          ),
        summaryRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
          history.push(
            RoutePath.generateRoutePath(RoutePath.EP_EDIT_SUBMISSION, { procedure, submissionId }),
            [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
          ),
        deleteRow: ids => ePCTApi.deleteSubmissions(ids),
        sendToSign: ids => ePCTApi.sendToSignSubmissions(ids),
        transferSubmissions: (ids, newOrgId) =>
          callApi(draftApi.transferSubmissions, {
            onSuccess: () => transferConfirmationDialog.open(false),
            onError: () => transferConfirmationDialog.open(true),
            showAlertOnError: false,
          })(ids, newOrgId),
        setPage: (value) => {
          setCookie({ name: 'pageEP', value });
          setPage(value);
        },
        setPageSize: (value) => {
          setCookie({ name: 'pageSizeEP', value });
          setPageSize(value);
        },
        getPage: () => Number(getCookie('pageEP')) || 0,
        getPageSize: () => Number(getCookie('pageSizeEP')) || PAGE_SIZES[0],
        deletePageCookie: () => {
          deleteCookie('pageEP');
          deleteCookie('pageSizeEP');
        },
        requestReceipt: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
          history.push(
            RoutePath.generateRoutePath(RoutePath.PROCEDURE_REQUEST_RECEIPT_SUBMISSION, { procedure, submissionId }),
            [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
          ),
        emptyTableIcon: <FolderIcon color="secondary" className={classes.emptyTableIcon} />,
        errorTableIcon: <ErrorIcon color="secondary" className={classes.errorTableIcon} />,
      },
      addressbook: {
        title: t('address-book.title'),
        description: t('messages.address-book.description'),
        button: <CreateContactButton />,

        loadData: () => callApi(addressBookApi.paginate)(page, pageSize, sort),

        editRow: ({ id: contactId }) =>
          history.push(RoutePath.generateRoutePath(RoutePath.ADDRESSBOOK_CONTACT_ID_EDIT, { contactId })),
        viewRow: ({ id: contactId }) =>
          history.push(RoutePath.generateRoutePath(RoutePath.ADDRESSBOOK_CONTACT_ID, { contactId })),
        sendToSign: () => {
          // not implemented yet
        },
        previewRow: () => {
          // not implemented yet
        },
        summaryRow: () => {
          // not implemented yet
        },
        deleteRow: ids => callApi(addressBookApi.deleteContacts)(ids),
        setPage: (value) => {
          setCookie({ name: 'pageAddressBook', value });
          setPage(value);
        },
        setPageSize: (value) => {
          setCookie({ name: 'pageSizeAddressBook', value });
          setPageSize(value);
        },
        getPage: () => Number(getCookie('pageAddressBook')) || 0,
        getPageSize: () => Number(getCookie('pageSizeAddressBook')) || PAGE_SIZES[0],
        deletePageCookie: () => {
          deleteCookie('pageAddressBook');
          deleteCookie('pageSizeAddressBook');
        },
        emptyTableIcon: <FolderIcon color="secondary" className={classes.emptyTableIcon} />,
        errorTableIcon: <ErrorIcon color="secondary" className={classes.errorTableIcon} />,
      },
    };
  }, [
    page,
    pageSize,
    filter,
    sort,
currentProcedure?.id,
columnsConfiguration,
  ]);

  const generateBackProps = (_procedure, _initialSubmissionProcedure) => ({
    goBackText: `${t(_procedure.label)}`,
    goBackPath: RoutePath.generateRoutePath(RoutePath.PROCEDURE_SEARCH, {
      procedure: _procedure.id,
      initialSubmissionProcedure: _initialSubmissionProcedure?.id || _initialSubmissionProcedure,
    }),
  });

  function createNationalProcedurePageStructure(button) {
    return {
      title: t(currentProcedure.label),
      description: t(currentProcedure.description),
      button,
      CreateSAButton,
      loadData: () => {
        dispatch(
          fetchSubmissionRows({
            procedure: !initialSubmissionProcedure ? currentProcedure.id : undefined,
            initialSubmissionProcedure,
            pagingSortingAndFiltering: {
              numberOfElementsOnAPage: pageSize,
              pageNumber: page,
              sortColumn: sort.sortColumn,
              sortOrder: sort.sortOrder,
              filter,
            },
          }),
        );
        const _procedure = !initialSubmissionProcedure ? currentProcedure.id : undefined;
        return draftApi.fetchSubmissionRows(_procedure, initialSubmissionProcedure, {
          numberOfElementsOnAPage: pageSize,
          pageNumber: page,
          sortColumn: sort.sortColumn,
          sortOrder: sort.sortOrder,
          filter,
        });
      },
      editRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
        history.push(
          RoutePath.generateRoutePath(RoutePath.PROCEDURE_EDIT, { procedure, submissionId }),
          [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
        ),
      payRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
        history.push(
          RoutePath.generateRoutePath(RoutePath.PROCEDURE_PAYMENT, { procedure, submissionId }),
          [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
        ),
      viewRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
        history.push(
          RoutePath.generateRoutePath(RoutePath.PROCEDURE_RECEIPTS_SUBMISSION, { submissionId, procedure }),
          [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
        ),
      createSARow: row => createSASubmission(row),
      previewRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
        history.push(
          RoutePath.generateRoutePath(RoutePath.PROCEDURE_PREVIEW_SUBMISSION, { procedure, submissionId }),
          [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
        ),
      summaryRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
        history.push(
          RoutePath.generateRoutePath(RoutePath.PROCEDURE_VIEW, { procedure, submissionId }),
          [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
        ),
      deleteRow: ids => callApi(draftApi.deleteSubmissions)(ids),
      sendToSign: ids => callApi(draftApi.sendToSignSubmissions)(ids),
      transferSubmissions: (ids, newOrgId) =>
        callApi(draftApi.transferSubmissions, {
          onSuccess: () => transferConfirmationDialog.open(false),
          onError: () => transferConfirmationDialog.open(true),
          showAlertOnError: false,
        })(ids, newOrgId),
      setPage: (value) => {
        setCookie({ name: `page${currentProcedure.id}`, value });
        setPage(value);
      },
      setPageSize: (value) => {
        setCookie({ name: `pageSize${currentProcedure.id}`, value });
        setPageSize(value);
      },
      getPage: () => Number(getCookie(`page${currentProcedure.id}`)) || 0,
      getPageSize: () => Number(getCookie(`pageSize${currentProcedure.id}`)) || PAGE_SIZES[0],
      deletePageCookie: () => {
        deleteCookie(`page${currentProcedure.id}`);
        deleteCookie(`pageSize${currentProcedure.id}`);
      },
      requestReceipt: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
        history.push(
          RoutePath.generateRoutePath(RoutePath.PROCEDURE_REQUEST_RECEIPT_SUBMISSION, { procedure, submissionId }),
          [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
        ),
      emptyTableIcon: <FolderIcon color="secondary" className={classes.emptyTableIcon} />,
      errorTableIcon: <ErrorIcon color="secondary" className={classes.errorTableIcon} />,
    };
  }

  function createWipoProcedurePageStructure(button) {
    return {
      title: t(currentProcedure.label),
      description: t(currentProcedure.description),
      button,
      loadData: () => {
        dispatch(
          fetchSubmissionRows({
            procedure: !initialSubmissionProcedure ? currentProcedure.id : undefined,
            initialSubmissionProcedure,
            pagingSortingAndFiltering: {
              numberOfElementsOnAPage: pageSize,
              pageNumber: page,
              sortColumn: sort.sortColumn,
              sortOrder: sort.sortOrder,
              filter,
            },
          }),
        );
        const _procedure = !initialSubmissionProcedure ? currentProcedure.id : undefined;
        return draftApi.fetchSubmissionRows(_procedure, initialSubmissionProcedure, {
          numberOfElementsOnAPage: pageSize,
          pageNumber: page,
          sortColumn: sort.sortColumn,
          sortOrder: sort.sortOrder,
          filter,
        });
      },
      editRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
        history.push(
          RoutePath.generateRoutePath(RoutePath.EPCT_EDIT_SUBMISSION, { procedure, submissionId }),
          [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
        ),
      viewRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
        history.push(
          RoutePath.generateRoutePath(RoutePath.PROCEDURE_RECEIPTS_SUBMISSION, { submissionId, procedure }),
          [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
        ),
      previewRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
        history.push(
          RoutePath.generateRoutePath(RoutePath.PROCEDURE_PREVIEW_SUBMISSION, { procedure, submissionId }),
          [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
        ),
      summaryRow: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
        history.push(
          RoutePath.generateRoutePath(RoutePath.PROCEDURE_VIEW, { procedure, submissionId }),
          [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
        ),
      deleteRow: ids => callApi(draftApi.deleteSubmissions)(ids),
      sendToSign: ids => callApi(draftApi.sendToSignSubmissions)(ids),
      transferSubmissions: (ids, newOrgId) =>
        callApi(draftApi.transferSubmissions, {
          onSuccess: () => transferConfirmationDialog.open(false),
          onError: () => transferConfirmationDialog.open(true),
          showAlertOnError: false,
        })(ids, newOrgId),
      setPage: (value) => {
        setCookie({ name: `page${currentProcedure.id}`, value });
        setPage(value);
      },
      setPageSize: (value) => {
        setCookie({ name: `pageSize${currentProcedure.id}`, value });
        setPageSize(value);
      },
      getPage: () => Number(getCookie(`page${currentProcedure.id}`)) || 0,
      getPageSize: () => Number(getCookie(`pageSize${currentProcedure.id}`)) || PAGE_SIZES[0],
      deletePageCookie: () => {
        deleteCookie(`page${currentProcedure.id}`);
        deleteCookie(`pageSize${currentProcedure.id}`);
      },
      requestReceipt: ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) =>
        history.push(
          RoutePath.generateRoutePath(RoutePath.PROCEDURE_REQUEST_RECEIPT_SUBMISSION, { procedure, submissionId }),
          [history?.location?.state, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
        ),
      emptyTableIcon: <FolderIcon color="secondary" className={classes.emptyTableIcon} />,
      errorTableIcon: <ErrorIcon color="secondary" className={classes.errorTableIcon} />,
    };
  }

  function createTableData(pagedData) {
    return pagedData.map(r => flattenObject(r));
  }

  const bulkRollBackStatus = async (ids) => {
    for (const id of ids) {
      const submissionStatusChangeModel = {
        previousStatus: fromStatus.id,
        currentStatus: toStatus.id,
        comments,
      };
      await callApi(draftApi.rollbackStatus)(id, submissionStatusChangeModel);
    }
  };

  const loadAndPrepareData = useCallback(async () => {
    setLoading(true);
    setSelection([]);
    if (userData && columnsConfiguration?.ready && location?.pathname !== RoutePath.DASHBOARD.path) {
      try {
        const pagedData = await pageStructure[currentProcedure.category].loadData();
        setColumns(
          GenericCellDataAdapter(
            columnsConfiguration?.resource,
            frontendCustomisationConfig,
            null,
            null,
            null,
            t,
            theming.theme,
            null,
            isExtraLargeOrBigger,
          ),
        );
        const tableData = createTableData(pagedData.content);
        setRows(tableData);
        setData(pagedData.content);
        setTotalCount(pagedData.totalElements);
        setError(false);
        setLoading(false);
      } catch (err) {
        setRows([]);
        setTotalCount(0);
        setLoading(false);
        setError(true);
      }
    }
  }, [
    setRows,
    filter,
    setTotalCount,
    setLoading,
    setError,
    pageStructure,
    setSort,
    pageSize,
    location,
    userData,
    columnsConfiguration?.ready,
  ]);

  const updatePage = (x) => {
    pageStructure[currentProcedure.category].setPage(x);
  };

  const updatePageSize = (x) => {
    pageStructure[currentProcedure.category].setPageSize(x);
  };

  const updatePageSizeAction = (x) => {
    tableQueryService.setPageSize(x);
    pageStructure[currentProcedure.category].setPageSize(x);
  };

  const updatePageAction = (x) => {
    tableQueryService.setPage(x);
    pageStructure[currentProcedure.category].setPage(x);
  };

  const loadMore = (x) => {
    const pageValue = 0;
    const pageSizeValue = pageSize + x;

    updatePage(pageValue);
    updatePageSize(pageSizeValue);
  };

  const loadLess = () => {
    const pageValue = 0;
    const pageSizeValue = PAGE_SIZE_GRID;

    updatePage(pageValue);
    updatePageSize(pageSizeValue);
  };

  const isRowSelected = row => selection.map(s => s.id).includes(row.id);

  const isAllSelected = () => rows.length > 0 && rows.every(row => isRowSelected(row));

  const selectRow = (row) => {
    setSelection(previousSelection => [...previousSelection, row]);
  };

  const unselectRow = (row) => {
    setSelection(previousSelection => previousSelection.filter(s => s.id !== row.id));
  };

  const selectAll = () => {
    setSelection(rows);
  };

  const unselectAll = () => {
    setSelection([]);
  };

  const handleSelectRow = row => () => {
    if (isRowSelected(row)) {
      unselectRow(row);
    } else {
      selectRow(row);
    }
  };

  const handleSelectAll = () => {
    if (isAllSelected()) {
      unselectAll();
    } else {
      selectAll();
    }
  };

  const handlePay = row => async () => {
    setAnchorActionMenu(null);
    if (!isLocked(row)) {
      try {
        pageStructure[currentProcedure.category].payRow(row);
      } catch (err) {
        loadAndPrepareData();
      }
    }
    if (isLocked(row)) {
      loadAndPrepareData();
    }
  };

  const handleEditDraft = row => async () => {
    setAnchorActionMenu(null);
    if (!isLocked(row)) {
      try {
        pageStructure[currentProcedure.category].editRow(row);
      } catch (err) {
        loadAndPrepareData();
      }
    }
    if (isLocked(row)) {
      loadAndPrepareData();
    }
  };

  function getNewPageNumber(numberOfDeletedSubmissions) {
    // page is zero based but numberOfPages is not.
    const numberOfPages = Math.ceil((totalCount - numberOfDeletedSubmissions) / pageSize) - 1;
    return page > numberOfPages ? numberOfPages : page;
  }

  const onDeleteDialogOpened = () => setAnchorActionMenu(null);

  const onDeleteDialogClosed = () => {
    // not implemented yet
  };

  const handleDeleteSubmissions = async (selectedSubmissionIds) => {
    await pageStructure[currentProcedure.category].deleteRow(selectedSubmissionIds);
    const newPageNumber = getNewPageNumber(selectedSubmissionIds.length);
    setSelection([]);
    if (newPageNumber === page) {
      loadAndPrepareData();
    }
    updatePage(newPageNumber);
  };

  const { deleteDialog } = useDeleteConfirmation(onDeleteDialogOpened, onDeleteDialogClosed, handleDeleteSubmissions);

  const onTransferDialogOpened = () => {
    setAnchorActionMenu(null);
  };

  const onTransferDialogClosed = () => {
    // not implemented yet
  };

  const handleTransferSubmissions = async (selectedSubmissionIds, destOrganizationId) => {
    await pageStructure[currentProcedure.category].transferSubmissions(selectedSubmissionIds, destOrganizationId);
  };

  const onTransferConfirmationDialogClosed = (movedSubmissionsCount) => {
    const newPageNumber = getNewPageNumber(movedSubmissionsCount);
    setSelection([]);
    if (newPageNumber === page) {
      loadAndPrepareData();
    }
    updatePage(newPageNumber);
  };

  const { transferDialog, transferConfirmationDialog } = useSubmissionTransfer(
    onTransferDialogOpened,
    onTransferDialogClosed,
    handleTransferSubmissions,
    onTransferConfirmationDialogClosed,
  );

  const sendSubmissions = async (selectedSubmissions) => {
    const currentRequestProgress = sendSubmissionRequestProgress || [];
    // eslint-disable-next-line no-plusplus
    for (let index = 0; index < selectedSubmissions.length; index++) {
      const existingSubmissionIndex = currentRequestProgress.findIndex(sub => sub.id === selectedSubmissions[index].id);
      if (existingSubmissionIndex > -1) {
        currentRequestProgress.splice(existingSubmissionIndex, 1);
      }

      await callApi(draftApi.sendToFiling, {
        onSuccess: () => {
          currentRequestProgress.push({
            success: true,
            submission: selectedSubmissions[index],
          });
        },
        onError: () => {
          currentRequestProgress.push({
            success: false,
            submission: selectedSubmissions[index],
          });
        },
      })(selectedSubmissions[index].id);
    }
    setSendSubmissionRequestProgess(currentRequestProgress);
    setSendSubmissionProcessDialogOpen(true);
    loadAndPrepareData();
  };

  const getSendSubmissionRequestErrors = sendSubmissionRequestProgress?.filter(
    requestProgress => !requestProgress.success,
  ).length;

  const retrySendSubmission = async (selectedSubmission) => {
    setAnchorActionMenu(null);
    await sendSubmissions(Array.isArray(selectedSubmission) ? selectedSubmission : [selectedSubmission]);
  };

  const handleChangeStatusSubmissions = async (selectedSubmissionIds) => {
    switch (toStatus.id) {
      case SubmissionStatus.DRAFT.id: {
        dispatch(startApplicationLoading());
        await bulkRollBackStatus(selectedSubmissionIds);
        dispatch(stopApplicationLoading());
        break;
      }

      case SubmissionStatus.READY_TO_SIGN.id: {
        dispatch(startApplicationLoading());
        if (fromStatus.id === SubmissionStatus.DRAFT.id) {
          await pageStructure[currentProcedure.category].sendToSign(selectedSubmissionIds);
        } else {
          await bulkRollBackStatus(selectedSubmissionIds);
        }
        dispatch(stopApplicationLoading());
        break;
      }

      default: {
        dispatch(startApplicationLoading());
        await bulkRollBackStatus(selectedSubmissionIds);
        dispatch(stopApplicationLoading());
        break;
      }
    }

    loadAndPrepareData();
  };

  const handleViewMenuClick = (row, event) => {
    if (row?.status !== SubmissionStatus.SENT.id && !isAddressBookPage) {
      setAnchorViewMenu(event?.currentTarget);
      setSelectedRow(row);
    } else {
      pageStructure[currentProcedure.category].viewRow(row);
    }
  };

  const handleViewMenuClose = () => {
    setAnchorViewMenu(null);
    setAnchorActionMenu(null);
  };

  const handlePreviewClick = async (row) => {
    setAnchorViewMenu(null);
    setAnchorActionMenu(null);
    pageStructure[currentProcedure.category].previewRow(row);

    await callApi(draftApi.lockById, {
      onError: (e) => {
        if (isLockError(e)) {
          history.replace(getSearchPageByProcedure(history));
        }
      },
    })(row.id);
  };

  const handleViewClick = row => () => {
    setAnchorActionMenu(null);
    pageStructure[currentProcedure.category].viewRow(row);
  };

  const handleCreateSAClick = row => () => {
    setAnchorActionMenu(null);
    pageStructure[currentProcedure.category].createSARow(row);
  };

  const onRetrySignClick = async (row) => {
    setAnchorViewMenu(null);
    setAnchorActionMenu(null);
    setAnchorChangeStatusMenu(null);

    await callApi(draftApi.retrySigning)(row.id, row.status.id === SubmissionStatus.DRAFT.id);
    loadAndPrepareData();
  };

  const onSendToSignClick = async (row) => {
    setAnchorViewMenu(null);
    setAnchorActionMenu(null);
    setAnchorChangeStatusMenu(null);

    await callApi(draftApi.sendToSign)(row.id, row.status.id === SubmissionStatus.DRAFT.id);
    loadAndPrepareData();
  };

  const onSendToPackageClick = async (row) => {
    setAnchorViewMenu(null);
    setAnchorActionMenu(null);
    setAnchorChangeStatusMenu(null);

    await callApi(draftApi.submitSignatures)(row.id);
    loadAndPrepareData();
  };

  const handleRequestReceipt = async (row) => {
    setAnchorViewMenu(null);
    setAnchorActionMenu(null);
    pageStructure[currentProcedure.category].requestReceipt(row);
  };

  const onSignClick = async ({ id: submissionId, procedure, initialSubmissionProcedure: isp }) => {
    setAnchorViewMenu(null);
    setAnchorActionMenu(null);

    const locationState = Array.isArray(history?.location?.state)
      ? history?.location?.state
      : [history?.location?.state];

    history.push(
      RoutePath.generateRoutePath(RoutePath.PROCEDURE_PREVIEW_SUBMISSION, { submissionId, procedure }),
      [...locationState, generateBackProps(getProcedure(procedure), getProcedure(isp))].filter(Boolean),
    );
  };

  const handleSummaryClick = async (row) => {
    setAnchorViewMenu(null);
    await callApi(draftApi.lockById)(row.id);
    if (!isWipoPage) {
      pageStructure[currentProcedure.category].summaryRow(row);
    }

    if (isWipoPage) {
      if (!isLocked(row)) {
        await callApi(ePCTApi.lockById, {
          onSuccess: () =>
            history.push(
              RoutePath.generateRoutePath(RoutePath.EPCT_EDIT_SUBMISSION, {
                submissionId: row.id,
                procedure: row.procedure,
              }),
            ),
          onError: () => loadAndPrepareData(),
        })(row.id);
      }
      if (isLocked(row)) {
        loadAndPrepareData();
      }
    }
  };

  const handleActionMenuClose = () => {
    setAnchorActionMenu(null);
  };

  const handleChangeStatusMenuClose = () => {
    setAnchorChangeStatusMenu(null);
  };

  const handleBackToDraftClick = () => {
    setAnchorActionMenu(null);
    setAnchorChangeStatusMenu(null);
    setIsRevertStatusDialogOpen(true);
    setRevertStatusAction(SubmissionStatus.DRAFT.id);
  };

  const handleResendSigningEmails = (submissionId) => {
    dispatch(getResendSigningEmailsThunk({ submissionId }));
    setAnchorActionMenu(null);
  };

  const handleBackToSignClick = () => {
    setAnchorActionMenu(null);
    setAnchorChangeStatusMenu(null);
    setIsRevertStatusDialogOpen(true);
    setRevertStatusAction(SubmissionStatus.READY_TO_SIGN.id);
  };

  const handleCloneSubmissionClick = row => async () => {
    setAnchorActionMenu(null);
    const sub = await callApi(draftApi.getById)(row.id);
    await callApi(draftApi.clone)(sub.id);
    await loadAndPrepareData();
  };

  const isEditDisabled = row => !isAddressBookPage && !canEdit(row);

  const isRowDeleteDisabled = row => !canDelete(row);

  const isDeleteDisabled = row =>
    !isAddressBookPage &&
    ((selection?.length >= 1 && selection.map(s => s.id).includes(row.id)) ||
      isLocked(row) ||
      isRowDeleteDisabled(row));

  const isDeleteSelectionDisabled = useMemo(() => {
    return selection.some(s => isRowDeleteDisabled(s)) || selection.some(s => isLocked(s));
  }, [selection]);

  const isPayDisabled = () => !userData?.roles.includes(Roles.SUBMISSION_PAYER);

  const hasDifferentStatus = (submissions, row) => {
    return submissions.filter(s => s.status !== row.status).length > 0;
  };

  const isChangeStatusSelectionDisabled = useMemo(
    () => selection.some(s => hasDifferentStatus(selection, s) || s.status === SubmissionStatus.SENT || isLocked(s)),
    [selection],
  );

  const isChangeStatusDisabled = useMemo(() => selection.some(s => !canChangeStatus(s)), [selection]);

  const isBackToDraftDisabled = useMemo(() => selection.some(s => !canRollbackToDraft(s)), [selection]);

  const isBackToReadyToSignDisabled = useMemo(() => selection.some(s => !canRollbackToReadyToSign(s)), [selection]);

  const isMoveSelectionDisabled = useMemo(() => {
    return (
      selection.some(s => isRowDeleteDisabled(s)) ||
      selection.some(s => isLocked(s)) ||
      !userData?.roles.includes(Roles.END_USER_ADMIN)
    );
  }, [selection]);

  const isSendSubmissionDisabled = !selection.every(
    s => s.status === SubmissionStatus.READY_TO_SEND.id && s.valid && !isLocked(s),
  );

  const sentSubmissions = sendSubmissionRequestProgress?.map(item => item.submission) || [];

  const getEditTooltipText = (row) => {
    if (isLocked(row)) {
      return t('messages.submission.locked');
    }

    if (row.status !== SubmissionStatus.DRAFT.id) {
      if (row?.status === 'SIGNED') {
        return t('messages.submission.lock.status').replace('{status}', t(SubmissionStatus?.READY_TO_SEND?.i18nKey));
      }
      return t('messages.submission.lock.status').replace('{status}', t(SubmissionStatus[row.status]?.i18nKey));
    }

    return t('actions.row.edit');
  };

  const getDeleteTooltipText = (row) => {
    if (isLocked(row)) return t('messages.submission.lock.delete');

    if (selection?.length > 1 && selection.map(s => s.id).includes(row.id)) return t('messages.submission.delete.bulk');

    if (isDeleteDisabled(row)) {
      if (row?.status === 'SIGNED') {
        return t('messages.submission.lock.status').replace('{status}', t(SubmissionStatus?.READY_TO_SEND?.i18nKey));
      }
      return t('messages.submission.lock.delete.status').replace('{status}', t(SubmissionStatus[row.status]?.i18nKey));
    }

    return t('actions.row.delete');
  };

  const getTransferTooltipText = (row) => {
    if (isLocked(row)) return t('messages.submission.lock.transfer');

    if (selection?.length > 1 && selection.map(s => s.id).includes(row.id))
      return t('messages.submission.transfer.bulk');

    return t('actions.row.transfer');
  };

  const getChangeStatusTooltipText = (submissions) => {
    if (submissions.some(s => isLocked(s)))
      return t('messages.submission.status.cannot-change-status-submission-edited-by-another-editor');

    if (submissions.some(s => hasDifferentStatus(submissions, s)))
      return t('messages.submission.status.cannot-change-status-submission-different-status');

    if (submissions.some(s => s.status === SubmissionStatus.SENT.id))
      return t('messages.submission.status.cannot-change-status-of-sent-submission');

    if (submissions.some(s => !s.valid))
      return t('messages.submission.status.cannot-change-status-submission-required-fields-incompleted');
    return '';
  };

  const getActionsTooltipText = () => t('messages.submission.actions.status');

  const confirmChangeStatus = submissions => () => {
    setIsChangeStatusDialogOpen(false);
    handleChangeStatusSubmissions(submissions.map(s => s.id));
    setDialogValues([]);
    unselectAll();
  };

  const openChangeStatusDialog = (submissions = [], newToStatus = SubmissionStatus.DRAFT) => () => {
    const { status } = submissions[0];
    setFromStatus(getSubmissionStatusById(status));
    setShowCommentsSection(false); // Hide Comments section on the Change Status Dialog for all statuses
    setToStatus(newToStatus);
    setAnchorActionMenu(null);
    setAnchorChangeStatusMenu(null);
    const values = submissions instanceof Array ? submissions : [submissions];
    setIsChangeStatusDialogOpen(true);
    setDialogValues(values);
  };

  const openSendSubmissionsDialog = (submissions = []) => {
    const values = submissions instanceof Array ? submissions : [submissions];
    setDialogValues(values);
    setAnchorActionMenu(null);
    setAnchorChangeStatusMenu(null);
    setSendSubmissionDialogOpen(true);
  };

  const closeChangeStatusDialog = () => {
    setDialogValues([]);
    setShowCommentsSection(false);
    setComments(null);
    setIsChangeStatusDialogOpen(false);
  };

  const confirmSendSubmissionsAction = submissions => () => {
    setSendSubmissionDialogOpen(false);
    sendSubmissions(submissions);
    setDialogValues([]);
    unselectAll();
  };

  const closeSendSubmissionDialog = () => {
    setDialogValues([]);
    setSendSubmissionDialogOpen(false);
  };

  const closeSendSubmissionProcessDialogOpen = () => {
    setSendSubmissionRequestProgess([]);
    setSendSubmissionProcessDialogOpen(false);
  };

  const closeCreateEPCTDialog = () => {
    setIsCreateEPCTDialogOpen(false);
  };
  const closeCreateEPDialog = () => {
    setIsCreateEPDialogOpen(false);
  };
  const closeCreateSPCDialog = () => {
    setIsCreateSPCDialogOpen(false);
  };

  const closeProcessingSignatureDialog = () => {
    const locationState = history.location?.state;
    if (locationState?.showProcessingDialog) delete locationState.showProcessingDialog;

    if (!_.isEmpty(history.location))
      history.replace({ ...history.location, state: _.isEmpty(locationState) ? undefined : locationState });
  };

  // TODO: The dialogs should have their own states, we should decouple them completely from this hook
  // Move all the dialogs related states to their own hooks, and use Redux to handle them!!!!

  const confirmCreateEPCT = (confirmData) => {
    history.replace({
      pathname: RoutePath.generateRoutePath(RoutePath.EPCT_FILING, { procedure: currentProcedure.id }),
      state: { epctCreationData: confirmData },
    });
    setIsCreateEPCTDialogOpen(false);
  };

  const confirmCreateEP = async (confirmData) => {
    history.replace({
      pathname: RoutePath.generateRoutePath(RoutePath.EP_FILING, { procedure: currentProcedure.id }),
      state: { epCreationData: confirmData },
    });
    setIsCreateEPDialogOpen(false);
  };

  const confirmCreateSPC = async (spcCreationData) => {
    dispatch(startApplicationLoading());
    const { userReference, extensionType, extentionForMedicinalType } = spcCreationData;

    const newSubmission = {
      basicFilingInfo: {
        userReference,
        requestType: extensionType,
        pediatricExtension: !!extentionForMedicinalType,
      },
    };

    try {
      const sub = await callApi(draftApi.create)({
        ...newSubmission,
        procedure: currentProcedure.id,
        receivingOffice: currentProcedure.receivingOffice,
      });
      await postProcess(sub, 'event_new_draft_created');

      setIsCreateSPCDialogOpen(false);
      history.push({
        pathname: RoutePath.generateRoutePath(RoutePath.PROCEDURE_EDIT, {
          submissionId: sub.id,
          procedure: sub.procedure,
        }),
        state: { spcCreationData },
      });
    } catch (err) {
      dispatch(stopApplicationLoading());
    }
  };

  const createSASubmission = async (row) => {
    dispatch(startApplicationLoading());

    let initialSubmission;
    const actionCell = columnsConfiguration?.resource.find(config => config?.type === CELL_TYPES.ACTION);
    const createSACustomRule = actionCell?.customRules?.createSubsequentAction;

    if (createSACustomRule) {
      /**
       * Returns a mapping for SA initial submission creation that should look like this
       * {
       *  basicFilingInfo: {
       *    userReference: _.get(row, 'basicFilingInfo.userReference'),
       *    applicationNumber: _.get(row, 'receipt.applicationNumber'),
       *  },
       *  initialSubmissionId: _.get(row, 'id'),
       *  initialSubmissionProcedure: _.get(row, 'procedure'),
       *  procedure: subsequentActions.resource.id,
       *  receivingOffice: subsequentActions.resource.receivingOffice,
       * }
       */
      initialSubmission = await callApi(draftApi.runCustomRule)(row, createSACustomRule, {
        procedure: subsequentActions.resource.id,
        receivingOffice: subsequentActions.resource.receivingOffice,
      });
    } else {
      initialSubmission = {
        basicFilingInfo: {
          userReference: _.get(row, 'basicFilingInfo.userReference'),
          applicationNumber: _.get(row, 'receipt.applicationNumber'),
        },
        initialSubmissionId: _.get(row, 'id'),
        initialSubmissionProcedure: _.get(row, 'procedure'),
        procedure: subsequentActions.resource.id,
        receivingOffice: subsequentActions.resource.receivingOffice,
      };
    }

    const dialogProps = {
      dialogProcedure: currentProcedure,
      uiSchemaProps: ConfigResourceProps.SUBMISSION_SA_UI_SCHEMA,
      validationSchemaProps: ConfigResourceProps.SUBMISSION_SA_VALIDATION_SCHEMA,
      submission: initialSubmission,
    };

    dispatch(openDialog(dialogProps));

    dispatch(stopApplicationLoading());
  };

  const retrySendingConfig = row => ({
    text: 'retry.sending',
    tooltipText: t('submissionPreview.tooltip.retrySending'),
    icon: <SendIcon />,
    onClick: () => retrySendSubmission(row),
  });

  const sendToSignConfig = (isDisabled, handleSendToSignSubmission) => ({
    text: 'send.to.sign',
    tooltipText: t('submissionPreview.tooltip.sendToSign'),
    icon: <SendIcon />,
    disabled: isDisabled,
    onClick: handleSendToSignSubmission,
  });

  const resendSigningEmailsConfig = (isDisabled, _handleResendSigningEmails) => ({
    text: 'submission.sign.resend.emails',
    tooltipText: null,
    icon: <People />,
    disabled: isDisabled,
    onClick: _handleResendSigningEmails,
  });

  const signConfig = (isDisabled, handleSignSubmission) => ({
    text: 'Sign',
    tooltipText: null,
    icon: <SignIcon />,
    disabled: isDisabled,
    onClick: handleSignSubmission,
  });

  const sendToPackageConfig = (isDisabled, handleSendToPackage) => ({
    text: 'confirm.signatures',
    tooltipText: null,
    icon: <PackageIcon />,
    disabled: isDisabled,
    onClick: handleSendToPackage,
  });

  const backToDraftConfig = (isDisabled, handleBackToDraftSubmission) => ({
    text: 'back.to.draft',
    tooltipText: null,
    icon: <UndoIcon />,
    disabled: isDisabled,
    onClick: handleBackToDraftSubmission,
  });

  const backToReadyToSignConfig = (isDisabled, handleBackToSignSubmission) => ({
    text: 'back.to.ready.to.sign',
    tooltipText: null,
    icon: <UndoIcon />,
    disabled: isDisabled,
    onClick: handleBackToSignSubmission,
  });

  const sendConfig = (isDisabled, handleSendSubmission) => ({
    text: 'actions.send.submission',
    tooltipText: null,
    icon: <SendIcon />,
    disabled: isDisabled,
    onClick: handleSendSubmission,
  });

  const retrySignConfig = (isDisabled, handleRetry) => ({
    id: 'actions.retry',
    text: 'actions.retry.signing',
    ariaLabel: 'actions.retry.signing',
    tooltipText: null,
    icon: <SignIcon />,
    disabled: isDisabled,
    onClick: handleRetry,
  });

  const editEntry = row => ({
    id: 'actions.row.edit',
    text: 'actions.submission.edit',
    ariaLabel: 'actions.row.edit',
    tooltipText: getEditTooltipText(row),
    icon: <EditIcon />,
    disabled: isEditDisabled(row),
    onClick: handleEditDraft(row),
  });

  const deleteEntry = row => ({
    id: 'actions.row.delete',
    ariaLabel: 'actions.row.delete',
    text: 'actions.submission.delete',
    tooltipText: getDeleteTooltipText(row),
    icon: <DeleteIcon />,
    disabled: isDeleteDisabled(row),
    onClick: deleteDialog.open(row),
  });

  const transferEntry = (isDisabled, row) => ({
    id: 'actions.row.transfer',
    ariaLabel: 'actions.row.transfer',
    text: 'actions.submission.transfer',
    tooltipText: getTransferTooltipText(row),
    icon: <ImportExport />,
    disabled: isDisabled,
    onClick: transferDialog.open(row),
  });

  const payEntry = row => ({
    text: 'actions.submission.pay',
    tooltipText: t('actions.submission.pay'),
    icon: <CreditCard />,
    disabled: isPayDisabled(),
    onClick: handlePay(row),
  });

  /*  const sendWithoutPaymentEntry = (isDisabled, handleSendSubmission) => ({
    text: 'actions.submission.send.without.payment',
    tooltipText: t('actions.submission.send.without.payment'),
    icon: <SendIcon />,
    disabled: isDisabled,
    onClick: handleSendSubmission,
  }); */

  const cloneEntry = row => ({
    text: 'actions.submission.clone',
    tooltipText: t('actions.submission.clone'),
    icon: <FileCopyIcon />,
    disabled: false,
    onClick: handleCloneSubmissionClick(row),
  });

  const viewEntry = row => ({
    id: 'actions.row.view',
    ariaLabel: 'actions.row.view',
    text: 'actions.submission.view',
    tooltipText: t('actions.row.view'),
    icon: <ViewIcon />,
    disabled: false,
    onClick: handleViewClick(row),
  });

  const viewMenuEntry = row => ({
    id: 'actions.row.view',
    ariaLabel: 'actions.row.view',
    text: 'actions.submission.view',
    tooltipText: t('actions.row.view'),
    icon: <ViewIcon />,
    active: row.id === submission.id && !!anchorViewMenu,
    disabled: false,
    onClick: (item, evt) => handleViewMenuClick(item, evt),
  });

  const createSAEntry = row => ({
    text: 'submission.subsequent-action.create',
    tooltipText: t('actions.row.create-subsequent-action'),
    icon: <SwapVert />,
    disabled: false,
    onClick: handleCreateSAClick(row),
  });

  const createPreviewEntry = row => ({
    icon: <InsertDriveFileIcon />,
    text: 'Preview',
    onClick: () => handlePreviewClick(row),
  });

  const requestReceiptEntry = row => ({
    text: 'actions.submission.request.receipt',
    icon: <SendIcon />,
    onClick: () => handleRequestReceipt(row),
  });

  const previewEntries = useCallback(
    (row) => {
      const entries = [
        {
          icon: <InsertDriveFileIcon />,
          text: 'Preview',
          onClick: () => handlePreviewClick(row),
        },
      ];
      if (!isWipoPage && !isEPPage) {
        entries.push({
          icon: <DashboardIcon />,
          text: 'view.summary',
          onClick: () => handleSummaryClick(row),
        });
      }
      return entries;
    },
    [isWipoPage],
  );

  const computeCombinedCellEntries = (row) => {
    const entries = [editEntry(row), deleteEntry(row), viewMenuEntry(row)];

    return entries;
  };

  const isOnlinePaid = row => _.get(row, 'paymentResponse.successful') === true;

  const computeActionEntries = (row) => {
    const entries = [];
    if (!isExtraLargeOrBigger) {
      if (row?.status !== SubmissionStatus.SENT.id) {
        entries.push(editEntry(row), deleteEntry(row), ...previewEntries(row));
      } else {
        entries.push(editEntry(row), deleteEntry(row), viewEntry(row));
      }
    }

    if (row.configurationVersion === configurationVersion.releaseVersion) {
      if (userData?.roles.includes(Roles.END_USER_ADMIN)) {
        entries.push(transferEntry(isLocked(row), row));
      }

      switch (row?.status) {
        case SubmissionStatus.DRAFT.id:
          if (userData?.roles.includes(Roles.SUBMISSION_DRAFTER)) {
            const isValid = _.get(row, 'filter.statusOrder') === SubmissionStatus.VALID_DRAFT.statusOrder;
            if (isValid) {
              entries.push(sendToSignConfig(false, () => onSendToSignClick(row)));
            }
          }
          break;

        case SubmissionStatus.READY_TO_SIGN.id:
          if (row['signatures.emailsNotSent']) {
            entries.push(resendSigningEmailsConfig(false, () => handleResendSigningEmails(row.id)));
          }
          entries.push(backToDraftConfig(isLocked(row) || !canRollbackToDraft(row), handleBackToDraftClick));
          if (userData?.roles.includes(Roles.SUBMISSION_SIGNER)) {
            entries.push(signConfig(false, () => onSignClick(row)));
          }
          if (userData?.roles.includes(Roles.SUBMISSION_DRAFTER)) {
            const isValid = _.get(row, 'filter.statusOrder') === SubmissionStatus.VALID_READY_TO_SIGN.statusOrder;
            if (isValid) {
              entries.push(sendToPackageConfig(isLocked(row), () => onSendToPackageClick(row)));
            }
          }
          break;

        case SubmissionStatus.SIGNING_FAILED.id:
          if (userData?.roles.includes(Roles.SUBMISSION_SIGNER)) {
            entries.push(retrySignConfig(false, onRetrySignClick));
          }
          entries.push(
            backToDraftConfig(!canRollbackToDraft(row), handleBackToDraftClick),
            backToReadyToSignConfig(!canRollbackToReadyToSign(row), handleBackToSignClick),
          );
          break;

        case SubmissionStatus.RESIGNING_FAILED.id:
          if (userData?.roles.includes(Roles.SUBMISSION_SIGNER)) {
            entries.push(retrySignConfig(false, onRetrySignClick));
          }
          break;

        case SubmissionStatus.READY_TO_SEND.id:
          if (userData?.roles.includes(Roles.SUBMISSION_SENDER)) {
            if (isOnlinePaid(row)) {
              entries.push(sendConfig(false, () => handlePreviewClick(row)));
            } else {
              entries.push(sendConfig(false, () => handlePreviewClick(row)));
            }
          }
          entries.push(
            backToDraftConfig(!canRollbackToDraft(row), handleBackToDraftClick),
            backToReadyToSignConfig(!canRollbackToReadyToSign(row), handleBackToSignClick),
          );
          break;

        case SubmissionStatus.SENDING_FAILED.id:
          if (userData?.roles.includes(Roles.SUBMISSION_SENDER)) {
            entries.push(retrySendingConfig(row));
          }
          if (row.paymentResponse == null) {
            entries.push(backToReadyToSignConfig(!canRollbackToReadyToSign(row), handleBackToSignClick));
          }
          break;

        case SubmissionStatus.SENT.id:
          if (
            subsequentActions.enabled &&
            !initialSubmissionProcedure &&
            userData?.roles.includes(Roles.SUBMISSION_DRAFTER)
          ) {
            entries.push(createSAEntry(row));
          }
          if (currentProcedure.confirmationRequestEnabled && userData?.roles.includes(Roles.SUBMISSION_SENDER)) {
            entries.push(requestReceiptEntry(row));
          }
          break;

        case SubmissionStatus.READY_TO_PAY.id:
          entries.push(
            payEntry(row),
            createPreviewEntry(row),
            backToDraftConfig(!canRollbackToDraft(row), handleBackToDraftClick),
            backToReadyToSignConfig(!canRollbackToReadyToSign(row), handleBackToSignClick),
          );
          break;

        default:
          break;
      }
    }

    if (
      isCloneEnabled(frontendCustomisationConfig) &&
      !isWipoPage &&
      !isEPPage &&
      hasRequiredRoles([Roles.END_USER_ADMIN, Roles.SUBMISSION_DRAFTER]) &&
      (row.status === SubmissionStatus.DRAFT.id || row.status === SubmissionStatus.VALID_DRAFT.id)
    ) {
      entries.push(cloneEntry(row));
    }

    return entries;
  };

  const setProperActionEntries = async (row) => {
    const entries = await computeActionEntries(row);
    setActionEntries(entries);
  };

  const setProperChangeStatusEntries = () => {
    const entries = [];

    entries.push(
      backToDraftConfig(isBackToDraftDisabled, openChangeStatusDialog(selection, SubmissionStatus.DRAFT)),
      backToReadyToSignConfig(
        isBackToReadyToSignDisabled,
        openChangeStatusDialog(selection, SubmissionStatus.READY_TO_SIGN),
      ),
    );

    // All selected rows are in a DRAFT status
    if (selection.filter(row => row.status === SubmissionStatus.DRAFT.id).length === selection.length) {
      entries.push(
        sendToSignConfig(isChangeStatusDisabled, openChangeStatusDialog(selection, SubmissionStatus.READY_TO_SIGN)),
      );
    }

    // All selected rows are in a READY_TO_SEND status
    if (selection.filter(row => row.status === SubmissionStatus.READY_TO_SEND.id).length === selection.length) {
      entries.push(sendConfig(isSendSubmissionDisabled, () => openSendSubmissionsDialog(selection)));
    }

    return setChangeStatusEntries(entries);
  };

  const handleActionMenuClick = row => (event) => {
    setAnchorActionMenu(event.currentTarget);
    setSelectedRow(row);
    setProperActionEntries(row);
  };

  const handleChangeStatusMenuClick = (event) => {
    setAnchorChangeStatusMenu(event.currentTarget);
    setProperChangeStatusEntries();
  };

  const closeRevertStatusDialog = () => {
    setIsRevertStatusDialogOpen(false);
    setRevertStatusAction(null);
  };

  const confirmRevertStatusDialog = () => {
    closeRevertStatusDialog();
    loadAndPrepareData();
  };

  const getSortOrderForColumn = column => (sort.sortColumn === column ? sort.sortOrder : SORT_ORDER.NONE);

  const toggleSortOrder = currentSortOrder => (currentSortOrder === SORT_ORDER.ASC ? SORT_ORDER.DSC : SORT_ORDER.ASC);

  const onClickColumn = column => () => {
    const newSort = {
      sortColumn: column,
      sortOrder: sort.sortColumn === column ? toggleSortOrder(sort.sortOrder) : SORT_ORDER.ASC,
    };

    setSort(newSort);
    tableQueryService.setSort(newSort);
  };

  useEffect(() => {
    setView(isSmallOrBigger && !isLargeOrBigger ? DRAFT_VIEW.GRID : DRAFT_VIEW.TABLE);
  }, [isLargeOrBigger]);

  useEffect(() => {
    if (filter?.length > 0) {
      tableQueryService.setFilter(filter);
    }
  }, [filter]);

  useEffect(() => {
    let newPage;
    let newPageSize;
    let newFilter = [];
    newPage = pageStructure[currentProcedure.category]?.getPage();
    newPageSize = pageStructure[currentProcedure.category]?.getPageSize();
    // First time useEffect runs
    if (!viewPrevious.current) {
      if (view === DRAFT_VIEW.GRID) {
        newPage = 0;
        newPageSize = PAGE_SIZE_GRID;
      }

      if (location?.state?.filter) {
        newFilter = location.state.filter || [];
      }

      if (tableQueryService?.filter?.length > 0) {
        newFilter = tableQueryService?.filter;
      }

      viewPrevious.current = view;
      updatePage(newPage);
      updatePageSize(newPageSize);
      setFilter(newFilter);

      return;
      // view has changed
    }
    if (viewPrevious.current !== view) {
      if (view === DRAFT_VIEW.GRID) {
        newPage = 0;
        newPageSize = PAGE_SIZE_GRID;
      } else {
        newPage = tableQueryService.page || 0;
        newPageSize = tableQueryService.pageSize || PAGE_SIZES[0];
      }

      viewPrevious.current = view;
      updatePage(newPage);
      updatePageSize(newPageSize);

      return;
      // loadData's dependencies have changed
    }

    const _sort = tableQueryService.parseSorting(tableQueryService.sort);
    if (_sort && sort?.sortColumn !== _sort?.sortColumn && sort?.sortOrder !== _sort?.sortColumn) {
      setSort(tableQueryService.parseSorting(tableQueryService.sort));
      return;
    }

    updatePage(newPage);
    updatePageSize(newPageSize);
    loadAndPrepareData();
  }, [loadAndPrepareData, view, location]);

  function unloadEvent() {
    pageStructure[currentProcedure.category].deletePageCookie();
  }

  useEffect(() => {
    window.addEventListener('beforeunload', unloadEvent);
    return () => {
      unloadEvent();
      window.removeEventListener('beforeunload', unloadEvent);
    };
  }, []);

  const emptyTableData = () => {
    if (error) {
      setEmptyTableWarningMessage(t('messages.submission.dont.have.error'));
      setEmptyTableSuggestion(t('submission.refresh.page'));
      return { icon: pageStructure[currentProcedure.category]?.errorTableIcon };
    }
    setEmptyTableWarningMessage(t('messages.submission.dont.have'));
    setEmptyTableSuggestion(t('submission.start.creating'));
    return { icon: pageStructure[currentProcedure.category]?.emptyTableIcon };
  };

  useEffect(() => {
    if (location?.state?.showProcessingDialog) {
      openAlertDialog({
        updatedTexts: {
          title: 'submission.overview.signedDialog.title',
          message: 'submission.overview.signedDialog.message',
          confirmButton: 'actions.ok',
          cancelButton: 'actions.cancel',
        },
        updatedSettings: {
          showCloseButton: true,
        },
        acceptHandler: {
          func: closeProcessingSignatureDialog,
        },
      });
    }
  }, [location]);

  return {
    handleDeleteSubmissions,
    handleChangeStatusSubmissions,
    page,
    pageSize,
    updatePage,
    updatePageSize,
    updatePageSizeAction,
    updatePageAction,
    isEPPage,
    totalCount,
    loadData: loadAndPrepareData,
    loadMore,
    loadLess,
    setSelection,
    selectedRow,
    anchorViewMenu,
    anchorActionMenu,
    anchorChangeStatusMenu,
    actionEntries,
    changeStatusEntries,
    previewEntries,
    columns,
    isEpvalPage,
    rows,
    data,
    setFilter,
    filter,
    loading,
    error,
    emptyTableWarningMessage,
    emptyTableSuggestion,
    selection,
    computeCombinedCellEntries,
    computeActionEntries,
    handleActionMenuClose,
    handleViewMenuClose,
    handleChangeStatusMenuClose,
    handleActionMenuClick,
    handleChangeStatusMenuClick,
    getActionsTooltipText,
    handleViewMenuClick,
    getDeleteTooltipText,
    getChangeStatusTooltipText,
    isDeleteDisabled,
    isRowDeleteDisabled,
    isDeleteSelectionDisabled,
    isPayDisabled,
    isChangeStatusSelectionDisabled,
    isMoveSelectionDisabled,
    isSendSubmissionDisabled,
    sentSubmissions,
    fromStatus,
    toStatus,
    handleEditDraft,
    isEditDisabled,
    getEditTooltipText,
    isAllSelected,
    isRowSelected,
    isLocked,
    handleSelectAll,
    handleSelectRow,
    view,
    setView,
    onClickColumn,
    getSortOrderForColumn,
    deleteDialog,
    isAddressBookPage,
    setShowImportContactDialog,
    showImportContactDialog,
    emptyTableData,
    isEPCTPage: isWipoPage,
    isSpc,
    sendSubmissionRequestProgress,
    createButton: pageStructure[currentProcedure.category]?.button,
    CreateSAButton: pageStructure[currentProcedure.category]?.CreateSAButton,
    tableConfiguration,
    revertStatusDialog: {
      isOpen: isRevertStatusDialogOpen,
      confirm: confirmRevertStatusDialog,
      cancel: closeRevertStatusDialog,
      revertStatusAction,
    },
    changeStatusDialog: {
      isOpen: isChangeStatusDialogOpen,
      open: openChangeStatusDialog,
      close: closeChangeStatusDialog,
      values: dialogValues,
      confirm: confirmChangeStatus,
      showCommentsSection,
      setComments,
      comments,
    },
    sendSubmissionDialog: {
      isOpen: isSendSubmissionDialogOpen,
      open: openSendSubmissionsDialog,
      close: closeSendSubmissionDialog,
      values: dialogValues,
      confirm: confirmSendSubmissionsAction,
    },
    transferDialog,
    transferConfirmationDialog,
    createEPDialog: {
      isOpen: isCreateEPDialogOpen,
      close: closeCreateEPDialog,
      confirm: confirmCreateEP,
    },
    createEPCTDialog: {
      isOpen: isCreateEPCTDialogOpen,
      close: closeCreateEPCTDialog,
      confirm: confirmCreateEPCT,
    },
    createSPCDialog: {
      isOpen: isCreateSPCDialogOpen,
      close: closeCreateSPCDialog,
      confirm: confirmCreateSPC,
    },
    sendSubmissionProcessDialog: {
      isOpen: isSendSubmissionProcessDialogOpen,
      close: closeSendSubmissionProcessDialogOpen,
      retry: retrySendSubmission,
      getSendSubmissionRequestErrors,
    },
    screenSize: {
      isMediumOrBigger,
      isLargeOrBigger,
      isExtraLargeOrBigger,
    },
  };
}

export const DraftManagementContext = React.createContext({});
